import React, { useState, useEffect } from 'react';
import { 
  PlayCircleIcon, 
  ChevronDownIcon, 
  ChevronRightIcon, 
  FileTextIcon, 
  MessageSquareIcon 
} from './Icons';

// --- Dados do Curso (Exemplo) ---
// Em uma aplicação real, isso viria de uma API.
export const courseContent = {
  title: "Previsões ENEM (ano corrente)",
  description: "Previsões e dicas para arrasar no ENEM deste ano.",
  progress: 0,
  modules: [
    {
      id: 1,
      title: "Módulo 1: Linguagens, Códigos e suas Tecnologias",
      lessons: [
        // Exemplo de link do Google Drive
        { id: 101, title: "Previsão de Linguagens - Parte 1", duration: "25:30", videoUrl: "https://drive.google.com/file/d/1A-B2C3D4E5F6G7H8I9J0K/view?usp=sharing" },
        { id: 102, title: "Previsão de Linguagens - Parte 2", duration: "28:45", videoUrl: "https://drive.google.com/file/d/ANOTHER_EXAMPLE_ID_HERE/view" },
        { id: 103, title: "Resumo de Literatura para o ENEM", duration: "22:15", videoUrl: "" },
      ]
    },
    {
      id: 2,
      title: "Módulo 2: Ciências Humanas e suas Tecnologias",
      lessons: [
        { id: 201, title: "Previsão de História", duration: "30:20", videoUrl: "" },
        { id: 202, title: "Previsão de Geografia", duration: "27:10", videoUrl: "" },
      ]
    },
    {
      id: 3,
      title: "Módulo 3: Ciências da Natureza e suas Tecnologias",
      lessons: [
        { id: 301, title: "Previsão de Biologia", duration: "35:00", videoUrl: "" },
        { id: 302, title: "Previsão de Física", duration: "32:45", videoUrl: "" },
        { id: 303, title: "Previsão de Química", duration: "31:20", videoUrl: "" },
      ]
    },
    {
      id: 4,
      title: "Módulo 4: Matemática e suas Tecnologias",
      lessons: [
        { id: 401, title: "Previsão de Matemática", duration: "29:30", videoUrl: "" },
        { id: 402, title: "Macetes e Dicas de Matemática", duration: "26:15", videoUrl: "" },
      ]
    }
  ]
};

export const CoursePlayerPage: React.FC = () => {
  const [activeModule, setActiveModule] = useState(1);
  const [activeLesson, setActiveLesson] = useState(101);
  const [activeTab, setActiveTab] = useState('overview');
  const [progress, setProgress] = useState(courseContent.progress);
  const [watchedLessons, setWatchedLessons] = useState([]);

  // Encontra a aula ativa
  const currentLesson = courseContent.modules
    .flatMap(m => m.lessons)
    .find(l => l.id === activeLesson);

  // --- Lógica do Google Drive ---
  // Extrai o ID do arquivo de um link padrão do Google Drive para construir a URL de embed.
  const getEmbedUrl = (videoUrl?: string) => {
    if (!videoUrl) return '';

    // Regex para capturar o ID entre /d/ e / (ou o fim da string)
    const match = videoUrl.match(/\/d\/(.*?)(?:\/|$)/);
    const driveId = match ? match[1] : null;

    if (driveId) {
      // URL oficial de preview do Google Drive que permite embed
      return `https://drive.google.com/file/d/${driveId}/preview`;
    }

    // Fallback: Retorna a URL original se não conseguir extrair, ou um placeholder se for inválido
    // Para fins de demonstração se a URL for placeholder:
    if (videoUrl.includes('EXAMPLE')) {
         // Retorna um vídeo placeholder do Youtube apenas para visualização no layout se o link for fake
         return `https://www.youtube.com/embed/dQw4w9WgXcQ?controls=0`;
    }
    
    return videoUrl; 
  };

  // Função para marcar aula como assistida e atualizar progresso
  const markLessonAsWatched = (lessonId: number) => {
    if (!watchedLessons.includes(lessonId)) {
      const updatedWatched = [...watchedLessons, lessonId];
      setWatchedLessons(updatedWatched);
      
      // Calcular progresso baseado em aulas assistidas
      const totalLessons = courseContent.modules.flatMap(m => m.lessons).length;
      const newProgress = Math.round((updatedWatched.length / totalLessons) * 100);
      setProgress(newProgress);
    }
  };

  // Efeito para marcar aula como assistida quando muda
  useEffect(() => {
    markLessonAsWatched(activeLesson);
  }, [activeLesson]);

  // Função para alternar módulo
  const toggleModule = (moduleId: number) => {
    if (activeModule === moduleId) {
      setActiveModule(0); // Fechar se já estiver aberto
    } else {
      setActiveModule(moduleId);
    }
  };

  // Função para mudar de aula
  const changeLesson = (lessonId: number) => {
    setActiveLesson(lessonId);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-[fadeIn_0.3s_ease-out]">
      
      {/* Cabeçalho do Curso */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#4c1d95]">{courseContent.title}</h1>
          <p className="text-gray-600 text-sm">{courseContent.description}</p>
        </div>
        
        {/* Card de Progresso */}
        <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 flex items-center gap-3">
          <div className="text-right">
             <span className="block text-xs text-gray-500 uppercase font-bold">Progresso</span>
             <span className="block text-[#7e5bef] font-bold">{progress}%</span>
          </div>
          {/* Gráfico circular simples com CSS */}
          <div className="relative w-12 h-12 rounded-full border-4 border-[#f3e8ff] flex items-center justify-center">
             <div 
               className="absolute inset-0 rounded-full border-4 border-t-[#7e5bef] border-r-[#7e5bef] border-b-transparent border-l-transparent transform -rotate-45"
               style={{ 
                 transform: `rotate(${(progress * 3.6) - 45}deg)`,
                 transition: 'transform 0.5s ease-in-out'
               }}
             ></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-auto lg:h-[calc(100vh-200px)] min-h-[600px]">
        
        {/* Coluna Principal: Vídeo e Abas */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          
          {/* Player de Vídeo */}
          <div className="bg-black rounded-2xl overflow-hidden shadow-lg aspect-video relative group border border-gray-800">
            {currentLesson?.videoUrl ? (
                <iframe 
                src={getEmbedUrl(currentLesson.videoUrl)} 
                className="w-full h-full" 
                title="Aula do Curso"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
                ></iframe>
            ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 bg-gray-900">
                    <PlayCircleIcon className="w-16 h-16 mb-2 opacity-50" />
                    <p>Selecione uma aula com vídeo disponível</p>
                    <p className="text-sm mt-2">Aula: {currentLesson?.title}</p>
                </div>
            )}
          </div>

          {/* Área de Conteúdo (Abas) */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex-grow flex flex-col">
            {/* Navegação das Abas */}
            <div className="flex border-b border-gray-100 overflow-x-auto">
              <button 
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'overview' ? 'border-[#7e5bef] text-[#7e5bef]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Visão Geral
              </button>
              <button 
                onClick={() => setActiveTab('materials')}
                className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'materials' ? 'border-[#7e5bef] text-[#7e5bef]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Materiais Complementares
              </button>
              <button 
                onClick={() => setActiveTab('comments')}
                className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'comments' ? 'border-[#7e5bef] text-[#7e5bef]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Dúvidas e Comentários
              </button>
            </div>

            {/* Conteúdo das Abas */}
            <div className="p-6 overflow-y-auto custom-scrollbar">
              {activeTab === 'overview' && (
                <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
                  <h3 className="text-xl font-bold text-gray-900">{currentLesson?.title}</h3>
                  <div className="prose prose-sm text-gray-600 max-w-none">
                    <p>
                      Bem-vindo à aula de <strong>{currentLesson?.title}</strong>. 
                      Nesta aula, focaremos nas previsões e dicas essenciais para garantir sua pontuação máxima no ENEM.
                    </p>
                    <p className="mt-2">
                        <strong>Objetivos da aula:</strong>
                    </p>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Compreender os temas mais prováveis de cair no ENEM.</li>
                        <li>Aplicar estratégias de resolução de questões.</li>
                        <li>Identificar pegadinhas comuns e como evitá-las.</li>
                    </ul>
                  </div>
                </div>
              )}
              
              {activeTab === 'materials' && (
                <div className="space-y-3 animate-[fadeIn_0.2s_ease-out]">
                  <div className="flex items-center p-4 rounded-lg border border-gray-100 hover:bg-[#fcfaff] hover:border-[#f3e8ff] transition-all cursor-pointer group">
                    <div className="bg-[#f3e8ff] p-3 rounded-lg text-[#7e5bef] mr-4">
                      <FileTextIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-grow">
                      <span className="block font-bold text-gray-900 text-sm">Previsões ENEM - Linguagens</span>
                      <span className="block text-xs text-gray-500 mt-1">PDF • 2.4 MB</span>
                    </div>
                    <div className="text-[#7e5bef] opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0">
                        <span className="text-sm font-bold">Baixar</span>
                    </div>
                  </div>

                  <div className="flex items-center p-4 rounded-lg border border-gray-100 hover:bg-[#fcfaff] hover:border-[#f3e8ff] transition-all cursor-pointer group">
                    <div className="bg-[#f3e8ff] p-3 rounded-lg text-[#7e5bef] mr-4">
                      <FileTextIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-grow">
                      <span className="block font-bold text-gray-900 text-sm">Resumo de Atualidades para o ENEM</span>
                      <span className="block text-xs text-gray-500 mt-1">PDF • 1.1 MB</span>
                    </div>
                    <div className="text-[#7e5bef] opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0">
                        <span className="text-sm font-bold">Baixar</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-4 rounded-lg border border-gray-100 hover:bg-[#fcfaff] hover:border-[#f3e8ff] transition-all cursor-pointer group">
                    <div className="bg-[#f3e8ff] p-3 rounded-lg text-[#7e5bef] mr-4">
                      <FileTextIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-grow">
                      <span className="block font-bold text-gray-900 text-sm">Macetes de Matemática para o ENEM</span>
                      <span className="block text-xs text-gray-500 mt-1">PDF • 1.8 MB</span>
                    </div>
                    <div className="text-[#7e5bef] opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0">
                        <span className="text-sm font-bold">Baixar</span>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'comments' && (
                <div className="animate-[fadeIn_0.2s_ease-out]">
                  <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <MessageSquareIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h4 className="text-gray-900 font-medium">Nenhuma dúvida ainda</h4>
                    <p className="text-gray-500 text-sm mt-1">Seja o primeiro a perguntar sobre esta aula!</p>
                    <button className="mt-4 px-6 py-2 bg-[#7e5bef] text-white rounded-lg text-sm font-bold hover:bg-[#6d28d9] transition-colors shadow-sm">
                        Enviar Dúvida
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coluna Lateral: Lista de Módulos (Accordion) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden h-full max-h-[calc(100vh-200px)] lg:max-h-full">
          <div className="p-4 bg-[#fcfaff] border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-bold text-gray-900">Conteúdo do Curso</h2>
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{courseContent.modules.length} Módulos</span>
          </div>
          
          <div className="overflow-y-auto flex-grow custom-scrollbar">
            {courseContent.modules.map((module) => (
              <div key={module.id} className="border-b border-gray-50 last:border-0">
                {/* Cabeçalho do Módulo */}
                <button 
                  onClick={() => toggleModule(module.id)}
                  className={`w-full flex items-center justify-between p-4 transition-colors text-left ${activeModule === module.id ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex flex-col">
                      <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1">Módulo 0{module.id}</span>
                      <span className="font-bold text-sm text-gray-800">{module.title}</span>
                  </div>
                  {activeModule === module.id ? (
                    <ChevronDownIcon className="w-5 h-5 text-[#7e5bef]" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {/* Lista de Aulas */}
                {activeModule === module.id && (
                  <div className="bg-[#fcfaff] py-2">
                    {module.lessons.map((lesson) => (
                      <button
                        key={lesson.id}
                        onClick={() => changeLesson(lesson.id)}
                        className={`w-full flex items-start gap-3 p-3 pl-6 transition-all text-left border-l-4 relative ${
                          activeLesson === lesson.id 
                            ? 'bg-[#f3e8ff]/40 border-[#7e5bef]' 
                            : 'border-transparent hover:bg-gray-100'
                        }`}
                      >
                        <div className={`mt-0.5 rounded-full p-1 flex-shrink-0 ${
                          watchedLessons.includes(lesson.id) 
                            ? 'text-green-500' 
                            : activeLesson === lesson.id 
                              ? 'text-[#7e5bef]' 
                              : 'text-gray-300'
                        }`}>
                          {watchedLessons.includes(lesson.id) ? (
                            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                          ) : activeLesson === lesson.id ? (
                            <PlayCircleIcon className="w-5 h-5 fill-current" />
                          ) : (
                            <PlayCircleIcon className="w-5 h-5" />
                          )}
                        </div>
                        <div className="flex-grow">
                          <span className={`block text-xs font-bold ${
                            activeLesson === lesson.id ? 'text-[#4c1d95]' : 'text-gray-600'
                          }`}>
                            {lesson.title}
                          </span>
                          <span className="block text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                             <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                             {lesson.duration}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default CoursePlayerPage;